import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { DefaultButton, Dropdown, IDropdownOption, Pivot, PivotItem, Stack, useTheme } from '@fluentui/react';
import { toast } from 'react-toastify';
import { buildStorefrontPatch, IStorefrontSettings, ShowLinkedItemsMode } from '../../../../models/Storefront';
import { globalMode, SHOW_LINKED_ITEMS_MODE_TEXT } from '../../../../logic/linkedItemsVisibility';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, usePatchStorefrontMutation, useRunSearchSuggestionsMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import { useGetVendorsQuery } from '../../../../store/Api';
import { useGetOrganizationQuery, useGetOrganizationEntitlementsQuery } from '../../../../store/endpoints/OrganizationsEndpoints';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import ToggleField from '../../../../components/Common/ToggleField';
import DropdownField from '../../../../components/Common/DropdownField';
import NumberField from '../../../../components/Inputs/NumberField';
import MarkupTypeSelection from '../../../../components/Storefront/MarkupTypeSelection';
import StorefrontProductSettingsForm from '../../../../components/Storefront/StorefrontProductSettingsForm';
import StorefrontAttributesEditor from '../../../../components/Storefront/StorefrontAttributesEditor';
import StorefrontProductsAdminTable from '../../../../components/Storefront/StorefrontProductsAdminTable';
import StorefrontPromotionsAdminTable from '../../../../components/Storefront/StorefrontPromotionsAdminTable';
import useCan, { PRODUCT_PROMOS_ADMIN } from '../../../../permissions/ability';
import StorefrontSavedSearchProductsAdminTable from '../../../../components/Storefront/StorefrontSavedSearchProductsAdminTable';
import StorefrontBundlesAdminTable from '../../../../components/Storefront/StorefrontBundlesAdminTable';
import { STOREFRONT_PRODUCTS_TABS } from '../../../../logic/StorefrontRoutes';
import { useUrlTabState } from '../../../../hooks/useUrlState';
import Setting from '../../../../components/Common/Setting';

interface StorefrontProductPageParams {
    storefront: string;
}

const filterStrategyOptions = [
    { key: 'exclude', text: 'Exclude' },
    { key: 'include', text: 'Include' },
];

const DEBOUNCE_MS = 3000;

const StorefrontProductsPage: React.FC<RouteComponentProps<StorefrontProductPageParams>> = (props) => {
    // UX gate only; the API forces both toggles off without the org entitlement regardless.
    const canManagePromos = useCan(PRODUCT_PROMOS_ADMIN);
    const { storefront } = props.match.params;
    // Deep link support: ?tab=products lands on the product list rather than the settings form, so
    // a "products added" toast can drop the user on what actually changed. The tab stays in the URL
    // as the user moves between tabs, which is what makes settings on each tab linkable.
    const [selectedTab, setSelectedTab] = useUrlTabState(STOREFRONT_PRODUCTS_TABS.settings);
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [patchStorefront] = usePatchStorefrontMutation();

    // The Frequently Added Together and More Like This enable toggles moved into the product-page
    // designer (Settings → Storefront → Pages → Product Page), where they live with the styling
    // controls for those rows. They are still entitlement-gated there.

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>();
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // The Settings tab autosaves, so a lease held by an assistant turn would turn each edit into a
    // rejected patch. One flag switches that tab off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', { skip: !selectedOrganization || selectedOrganization === '' });
    const vendors = useGetVendorsQuery(organization.data?.defaultCatalog ?? '', { skip: !organization.data?.defaultCatalog });

    const theme = useTheme();
    // Search suggestions are entitlement-gated. The API forces the toggle off without it regardless;
    // this only decides whether the control is worth showing.
    const entitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', { skip: !selectedOrganization || selectedOrganization === '' });
    const canAutocomplete = entitlements.data?.searchAutocompleteEnabled === true;
    const [runSuggestions, runSuggestionsState] = useRunSearchSuggestionsMutation();
    const queueSuggestionsRun = async () => {
        try {
            // Scoped to THIS storefront: the merchant is on its settings page, and rebuilding the whole
            // organization as a side effect would be a surprising amount of work to kick off from here.
            const result = await runSuggestions(storefront).unwrap();
            if (result.success) toast.success(result.message ?? 'Suggestions rebuild queued');
            else toast.error(result.message ?? 'Could not queue the suggestions rebuild');
        } catch {
            toast.error('Could not queue the suggestions rebuild');
        }
    };

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchStorefront({ id: storefront, patch });
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    }, [patchStorefront, storefront]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleVendorChange = async (ev: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
        if (!item) return;
        const current = localSettingsRef.current ?? localSettings;
        const currentVendors = current.storefrontVendors ?? [];
        const newVendors = item.selected
            ? [...currentVendors, item.key as string]
            : currentVendors.filter(v => v !== item.key);
        const updated = { ...current, storefrontVendors: newVendors };
        setLocalSettings(updated);
        try {
            await patchStorefront({ id: storefront, patch: { storefrontVendors: newVendors } }).unwrap();
            if (originalSettingsRef.current) {
                originalSettingsRef.current = { ...originalSettingsRef.current, storefrontVendors: newVendors };
            }
            toast.success('Settings saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    };

    const globalSettings = globalSettingsQuery.data;

    return (
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title="Storefront Products"
                onSettingsChanged={handleSettingsChanged}
            />
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                {/* Only this tab patches the storefront itself, so only this tab is switched off. The
                    Products / Saved Searches / Bundles tabs manage the storefront's product listing
                    through their own endpoints, which the edit lease does not guard. */}
                <PivotItem headerText='Settings' itemKey={STOREFRONT_PRODUCTS_TABS.settings}>
                    <StorefrontReadOnlyRegion readOnly={readOnly}>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <h2>Markup</h2>
                        <Setting id='storefront.products.defaultMarkup'>
                            <NumberField
                                label='Default Markup'
                                decimal={true}
                                value={localSettings.defaultMarkup}
                                placeholder={globalSettings?.defaultMarkup?.toString() ?? 'Not Set'}
                                onChange={(v) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), defaultMarkup: v };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <Setting id='storefront.products.defaultMarkupType'>
                            <MarkupTypeSelection
                                value={localSettings.defaultMarkupType}
                                onChange={(v) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), defaultMarkupType: v };
                                    setLocalSettings(updated);
                                    scheduleSave();
                                }}
                                label='Markup Type'
                            />
                        </Setting>
                        <Setting id='storefront.products.ignoreInheritedProducts'>
                            <ToggleField
                                inline
                                label='Ignore Inherited Products'
                                checked={localSettings.ignoreInheritedProducts}
                                onChange={(e, checked) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), ignoreInheritedProducts: checked ?? false };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        {/* Promos sit under Markup because that is what they change: a promo replaces the
                            markup above, or prices from a distributor's promotional cost instead of their
                            normal one. Kept as two switches because a vendor promotion moves the shopper
                            price with no action from the MSP, so a storefront needs to be able to run its
                            own promos while opting out of that. */}
                        {canManagePromos && <>
                            <Setting id='storefront.products.honorVendorPromos'>
                                <ToggleField
                                    inline
                                    label='Honor Vendor Promos'
                                    toolTip="Pass a distributor's promotional cost through to the shopper at your usual markup. Prices move when the vendor runs a promotion, without any action from you."
                                    checked={localSettings.enableVendorPromos}
                                    onChange={(e, checked) => {
                                        const updated = { ...(localSettingsRef.current ?? localSettings), enableVendorPromos: checked ?? false };
                                        setLocalSettings(updated);
                                        doSave(updated);
                                    }}
                                />
                            </Setting>
                            <Setting id='storefront.products.honorProductPromos'>
                                <ToggleField
                                    inline
                                    label='Honor Product Promos'
                                    toolTip="Apply promos you create on products, which reduce the price out of your own margin. The shopper always gets whichever price is lower, and promos never stack with a vendor promotion."
                                    checked={localSettings.enableMspPromos}
                                    onChange={(e, checked) => {
                                        const updated = { ...(localSettingsRef.current ?? localSettings), enableMspPromos: checked ?? false };
                                        setLocalSettings(updated);
                                        doSave(updated);
                                    }}
                                />
                            </Setting>
                        </>}
                        <h2>Vendor Filters</h2>
                        <Setting id='storefront.products.vendorFilter'>
                            <Dropdown
                                placeholder='Select Vendors'
                                multiSelect={true}
                                label='Storefront Vendor Filter'
                                selectedKeys={localSettings.storefrontVendors}
                                options={vendors?.data?.items?.map(vendor => ({ key: vendor, text: vendor })) ?? []}
                                onChange={handleVendorChange}
                                disabled={vendors.isLoading || vendors.isFetching}
                            />
                        </Setting>
                        <Setting id='storefront.products.vendorFilterStrategy'>
                            <Dropdown
                                placeholder='Select Filter Strategy'
                                multiSelect={false}
                                label='Vendor Filter Strategy'
                                selectedKey={localSettings.vendorFilterStrategy ?? 'include'}
                                options={filterStrategyOptions}
                                onChange={(e, v) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), vendorFilterStrategy: v?.key.toString() };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <h2>Product Display</h2>
                        <Setting id='storefront.products.showLinkedItems'>
                            <DropdownField
                                label='Show Linked Items on Storefront'
                                selectedKey={localSettings.showLinkedItemsMode ?? ''}
                                options={[
                                    { key: '', text: `Inherit Global (${SHOW_LINKED_ITEMS_MODE_TEXT[globalMode(globalSettings)]})` },
                                    { key: 'all', text: 'All Products' },
                                    { key: 'select', text: 'Select Products' },
                                    { key: 'disabled', text: 'Disabled' },
                                ]}
                                onChange={(e, v) => {
                                    const mode = v?.key === '' ? null : (v?.key as ShowLinkedItemsMode);
                                    const updated = { ...(localSettingsRef.current ?? localSettings), showLinkedItemsMode: mode };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <Setting id='storefront.products.allowProductVideo'>
                            <ToggleField
                                inline
                                toolTip='If enabled, products that have video URLs show a play button on their image that opens a video player.'
                                label='Allow Playing Product Video'
                                checked={localSettings.allowProductVideoPlayback}
                                onChange={(e, checked) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), allowProductVideoPlayback: checked ?? false };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <Setting id='storefront.products.enableImageGallery'>
                            <ToggleField
                                inline
                                toolTip='If enabled, the product details page shows a gallery so shoppers can scroll through all of a product’s images.'
                                label='Enable Product Image Gallery'
                                checked={localSettings.enableProductImageGallery}
                                onChange={(e, checked) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), enableProductImageGallery: checked ?? false };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <Setting id='storefront.products.enableProductTags'>
                            <ToggleField
                                inline
                                toolTip='If enabled, product tags are shown as colorful decorations on product cards and rows, and shoppers can filter search results by tag.'
                                label='Enable Product Tags'
                                checked={localSettings.enableProductTags}
                                onChange={(e, checked) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), enableProductTags: checked ?? false };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <Setting id='storefront.products.enableFavorites'>
                            <ToggleField
                                inline
                                toolTip='If enabled, shoppers can favorite products with a heart icon and filter search results down to their favorites. Requires the shopper to be signed in.'
                                label='Enable Favorites'
                                checked={localSettings.enableProductFavorites}
                                onChange={(e, checked) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), enableProductFavorites: checked ?? false };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        {canAutocomplete && <>
                            <Setting id='storefront.products.buildSearchSuggestions'>
                                <ToggleField
                                    inline
                                    toolTip={'If enabled, search suggestions are built nightly for this storefront from its catalog and shopper activity. '
                                        + 'Turning this on does not show them anywhere by itself — each search bar decides whether to display a suggestion '
                                        + 'panel, in the storefront designer.'}
                                    label='Build Search Suggestions'
                                    checked={localSettings.enableSearchAutocomplete}
                                    onChange={(e, checked) => {
                                        const updated = { ...(localSettingsRef.current ?? localSettings), enableSearchAutocomplete: checked ?? false };
                                        setLocalSettings(updated);
                                        doSave(updated);
                                    }}
                                />
                            </Setting>
                            {localSettings.enableSearchAutocomplete && <div>
                                <DefaultButton
                                    text={runSuggestionsState.isLoading ? 'Queueing…' : 'Rebuild Suggestions Now'}
                                    iconProps={{ iconName: 'Sync' }}
                                    disabled={runSuggestionsState.isLoading}
                                    onClick={queueSuggestionsRun}
                                />
                                <div style={{ color: theme.palette.neutralSecondary, fontSize: 12, marginTop: '0.5em' }}>
                                    Rebuilds this storefront&apos;s search suggestions from its current catalog. This
                                    normally runs on a schedule during low-usage hours; use this to pick up a large
                                    catalog change straight away. The run happens in the background and can take a few
                                    minutes, so suggestions will not change the instant it finishes queueing.
                                </div>
                            </div>}
                        </>}
                    </Stack>
                    </StorefrontReadOnlyRegion>
                </PivotItem>
                <PivotItem headerText='Attributes' itemKey={STOREFRONT_PRODUCTS_TABS.attributes}>
                    <StorefrontReadOnlyRegion readOnly={readOnly}>
                    <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { paddingTop: '0.5em' } }}>
                        <StorefrontAttributesEditor
                            storefront={storefront}
                            value={localSettings.attributeSettings}
                            onChange={(next) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), attributeSettings: next };
                                setLocalSettings(updated);
                                doSave(updated);
                            }}
                        />
                    </Stack>
                    </StorefrontReadOnlyRegion>
                </PivotItem>
                <PivotItem headerText='Products' itemKey={STOREFRONT_PRODUCTS_TABS.products}>
                    <StorefrontProductsAdminTable storefront={storefront} />
                </PivotItem>
                <PivotItem headerText='Saved Searches' itemKey={STOREFRONT_PRODUCTS_TABS.savedSearches}>
                    <StorefrontSavedSearchProductsAdminTable storefront={storefront} />
                </PivotItem>
                <PivotItem headerText='Bundles' itemKey={STOREFRONT_PRODUCTS_TABS.bundles}>
                    <StorefrontBundlesAdminTable storefront={storefront} />
                </PivotItem>
                {/* Gated on the same permission as creating one. The permission is itself stripped
                    unless the organization holds the promos entitlement, and the API checks it again.

                    Uses the value hoisted at the top of the component, never useCan(...) inline: CAN calls
                    useSelector internally, so calling it here would run a hook conditionally and after
                    every other one, which breaks hook order the moment this branch changes. Its name
                    gives no hint that it is a hook, so neither tsc nor rules-of-hooks catches it. */}
                {canManagePromos && (
                    <PivotItem headerText='Promotions' itemKey={STOREFRONT_PRODUCTS_TABS.promotions}>
                        <StorefrontPromotionsAdminTable storefront={storefront} />
                    </PivotItem>
                )}
            </Pivot>
        </div>
    );
};

export default StorefrontProductsPage;